// Overlay dev-facing sobre el video. Solo se renderiza cuando showDebug está
// activo en SesionCamara. Dos paneles posicionados absolute dentro del area
// de cámara: valores en vivo (top-right) y log de reps por serie (bottom-right).

import { useEffect, useRef } from "react";
import type {
  EvaluationResult,
  ExerciseRule,
  ExerciseSchema,
  RepResult,
  RuleEvaluation,
} from "@/lib/pose/types";

interface DetectorDebugHUDProps {
  evaluation: EvaluationResult | null;
  fps: number;
  schema: ExerciseSchema;
  completedRepsBySerie: RepResult[][];
  currentSerie: number;
}

export function DetectorDebugHUD({
  evaluation,
  fps,
  schema,
  completedRepsBySerie,
  currentSerie,
}: DetectorDebugHUDProps) {
  return (
    <>
      <LiveValuesPanel evaluation={evaluation} fps={fps} schema={schema} />
      <RepLogPanel
        completedRepsBySerie={completedRepsBySerie}
        currentSerie={currentSerie}
      />
    </>
  );
}

// ─── Live values panel (top-right) ──────────────────────────────────────────

function LiveValuesPanel({
  evaluation,
  fps,
  schema,
}: {
  evaluation: EvaluationResult | null;
  fps: number;
  schema: ExerciseSchema;
}) {
  return (
    <div className="absolute top-12 right-3 w-[200px] bg-black/70 backdrop-blur-sm rounded-lg p-3 font-mono text-[11px] text-green-400 pointer-events-auto">
      <div className="text-zinc-500 uppercase tracking-wider text-[10px] mb-2 border-b border-zinc-800 pb-1">
        live
      </div>
      <p>
        <span className="text-zinc-500">fps:</span> {fps}
      </p>
      <p>
        <span className="text-zinc-500">phase:</span>{" "}
        {evaluation?.repPhase ?? "—"}
      </p>
      <p>
        <span className="text-zinc-500">reps:</span> {evaluation?.repCount ?? 0}
      </p>
      <div className="mt-2 pt-2 border-t border-zinc-800 space-y-0.5">
        {schema.rules.map((rule, idx) => (
          <RuleRow
            key={idx}
            rule={rule}
            ruleEval={evaluation?.ruleEvaluations[idx]}
          />
        ))}
        {evaluation?.debug && (
          <DebugValuesBlock debug={evaluation.debug} />
        )}
      </div>
    </div>
  );
}

// ─── Debug values (evaluator-provided) ──────────────────────────────────────
// Muestra los pares label:value que el evaluator expuso en EvaluationResult.debug.
// Cada evaluator decide qué es útil para su calibración; el HUD los renderea
// sin conocer semántica. Usado para calibración del NeckLateralFlexionEvaluator
// (Sprint 3 spec-medicion Bloque 5, 12/7/2026).
function DebugValuesBlock({
  debug,
}: {
  debug: Record<string, number | string>;
}) {
  return (
    <div className="mt-1 pt-1 border-t border-zinc-800/50">
      {Object.entries(debug).map(([label, value]) => (
        <p key={label} className="truncate">
          <span className="text-zinc-500">{label}:</span>{" "}
          <span>{typeof value === "number" ? value.toString() : value}</span>
        </p>
      ))}
    </div>
  );
}

function RuleRow({
  rule,
  ruleEval,
}: {
  rule: ExerciseRule;
  ruleEval: RuleEvaluation | undefined;
}) {
  const value = ruleEval?.currentValue;
  const target = formatRuleTarget(rule);
  const passed = ruleEval?.passed;
  return (
    <p className="truncate">
      <span className="text-zinc-500">{rule.violationId}:</span>{" "}
      {value !== undefined ? value.toFixed(2) : "—"}
      <span className="text-zinc-600"> / {target}</span>
      {passed === true && <span className="ml-1 text-green-500">✓</span>}
      {passed === false && <span className="ml-1 text-red-400">✗</span>}
    </p>
  );
}

function formatRuleTarget(rule: ExerciseRule): string {
  switch (rule.type) {
    case "angle_range":
      return `${rule.min}–${rule.max}°`;
    case "horizontal_distance_max":
      return `≤${rule.maxDistance}`;
    case "trunk_stability":
      return `Δ≤${rule.maxVariationDeg}°`;
    case "vertical_alignment_max_deg":
      return `≤${rule.maxDeviationDeg}° vert`;
  }
}

// ─── Rep log panel (bottom-right) ───────────────────────────────────────────

function RepLogPanel({
  completedRepsBySerie,
  currentSerie,
}: {
  completedRepsBySerie: RepResult[][];
  currentSerie: number;
}) {
  const totalReps = completedRepsBySerie.reduce((sum, s) => sum + s.length, 0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll al fondo cuando llega una rep nueva, para que la última
  // siempre quede visible. Si el usuario hizo scroll manual hacia arriba,
  // esto lo "interrumpe" y vuelve al bottom — aceptable para diagnóstico.
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [totalReps]);

  // max-h en vh (viewport-relative) en lugar de % (parent-relative): el parent
  // overlay tiene aspect-video que en mobile portrait puede no resolver bien
  // su height computada, y % se vuelve indefinido. vh es siempre resoluble.
  return (
    <div
      ref={containerRef}
      className="absolute bottom-3 right-3 w-[240px] max-h-[40vh] overflow-y-auto bg-black/70 backdrop-blur-sm rounded-lg p-3 font-mono text-[11px] text-green-400 pointer-events-auto"
    >
      <div className="sticky top-0 bg-black/70 backdrop-blur-sm text-zinc-500 uppercase tracking-wider text-[10px] mb-2 border-b border-zinc-800 pb-1 flex items-center justify-between">
        <span>reps log</span>
        <span>{totalReps} total</span>
      </div>
      {completedRepsBySerie.map((reps, serieIdx) => (
        <div key={serieIdx} className="mb-2 last:mb-0">
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider">
            serie {serieIdx + 1}
            {serieIdx + 1 === currentSerie && (
              <span className="text-amber-400 normal-case ml-1">← current</span>
            )}
          </p>
          {reps.length === 0 ? (
            <p className="text-zinc-600">—</p>
          ) : (
            reps.map((rep) => <RepRow key={rep.n} rep={rep} />)
          )}
        </div>
      ))}
    </div>
  );
}

function RepRow({ rep }: { rep: RepResult }) {
  const dur = (rep.durationMs / 1000).toFixed(1);
  const q = rep.quality.toFixed(2);
  return (
    <p className="truncate">
      <span className="text-zinc-500">#{rep.n}</span>{" "}
      <span>{rep.angleMax.toFixed(0)}°</span>{" "}
      <span className="text-zinc-600">{dur}s</span>{" "}
      <span className={qualityColor(rep.quality)}>q={q}</span>
      {rep.violations.length > 0 && (
        <span className="text-red-400 ml-1">!{rep.violations.length}</span>
      )}
    </p>
  );
}

function qualityColor(q: number): string {
  if (q >= 0.8) return "text-green-400";
  if (q >= 0.5) return "text-amber-400";
  return "text-red-400";
}
